import {Component, EventEmitter, Input, Output} from '@angular/core';
import { ConstraintService } from "../../shared/services/constraint.service";
import {UtilService} from "../../shared/services/util.service";
import { InfoComponent } from "../info/info.component";
import { MaterialModule } from "src/app/app.materials";
import { FormsModule } from '@angular/forms';
import { FilterSelectComponent } from "../filter-select/filter-select.component";
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-constraint-setter',
  templateUrl: './constraint-setter.component.html',
  styleUrls: ['./constraint-setter.component.scss'],
  imports: [InfoComponent, MaterialModule, FormsModule, FilterSelectComponent, CommonModule],
  standalone: true,
})
export class ConstraintSetterComponent {

  @Input() sectors: any[] = []
  @Input() countries: any[] = []
  @Input() assets: any[] = []
  @Input() portfolio: any;
  @Input() constraints: any[] = []
  @Input() isSummary: boolean = false
  @Output() optimize = new EventEmitter();

  shorts = false;

  activeCons: any = {
    saveEach: true,
    items: [],
  };
  list: any[] = [];
  showOptimize = false;

  types = [
    { id: 'asset', name: 'Asset' },
    { id: 'sector', name: 'Sector' },
    { id: 'country', name: 'Country' }
  ]

  operators = ['=', '>', '<', '<=', '>=']

  constructor(
    private service: ConstraintService,
    private util: UtilService
  ) { }


  onSaveEachChange() {
    this.activeCons.saveEach = !this.activeCons.saveEach;
  }

  onTypeChange() {
    this.list = []
    switch (this.activeCons.type) {
      case 'asset':
        this.list = this.assets;
        break;
      case 'sector':
        this.list = this.sectors;
        break;
      case 'country':
        this.list = this.countries;
        break;
      default:
        this.list = [];
    }
  }

  initActiveCon()
  {
    return {
      type: 'asset',
      portfolio_id: this.portfolio.id,
      saveEach: true,
      items: [],
    }
  }

  onAddConstraint() {
    this.activeCons = this.initActiveCon()
    this.list = this.assets;
    if (this.activeCons.items && this.activeCons.items.length > 0) {
      this.saveCon(false)
    }
  }

  doBulkSave(c) {
    this.service.create(c).subscribe(resp => {
      if (resp.error) {
        return this.service.notificationService.open(resp.error);
      }
      if (c.id) {
        this.constraints = this.util.findAndReplaceItemInArray(resp, this.constraints);
      } else {
        this.constraints.push(resp);
      }
      this.showOptimize = true;
      this.activeCons = this.initActiveCon()
      this.list = this.assets;
    })
  }

  saveEachCon() {

    const con: any = {};
    this.activeCons.items.map(i => {
      con.type = this.activeCons.type;
      con.portfolio_id = this.activeCons.portfolio_id;
      con.items = [i];
      con.min = this.activeCons.min;
      con.max = this.activeCons.max;

      this.doBulkSave(con);
    })
  }

  saveCon(discard = true) {

    if (!this.validateActiveConstraintValues()) {
      return;
    }

     const projectedConstraints = this.getProjectedConstraintsSnapshot();
     let sum_min = projectedConstraints.reduce((partialSum, a) => partialSum + (+a.min || 0), 0);
     let sum_max = projectedConstraints.reduce((partialSum, a) => partialSum + (+a.max || 0), 0);
     let intersect = (this.portfolio?.securities || []).filter(s => projectedConstraints.some(p => this.normalizeConstraintType(p) === 'asset' && this.getConstraintItemIds(p).includes(+s.id)));
    
     if(sum_min >100){
        this.service.notificationService.open("Sum of minimum constraints cannot exceed 100%")
        return;
     }
     if(sum_max < 100 && intersect.length == (this.portfolio?.securities?.length || 0)){
        this.service.notificationService.open("Sum of maximum constraints must be at least 100%")
        return;
     }
     if (this.hasImpossibleAssetMax(projectedConstraints)) {
        this.service.notificationService.open("Asset max constraints must allow at least 100% in total")
        return;
     }


    if (this.activeCons.saveEach) {
      return this.saveEachCon();
    }

    if (this.activeCons.type && this.activeCons.items) {
      this.service.create(this.activeCons).subscribe(resp => {
        if (resp.error) {
          return this.service.notificationService.open(resp.error);
        }
        if (this.activeCons.id) {
          this.constraints = this.util.findAndReplaceItemInArray(resp, this.constraints);
        } else {
          this.constraints.push(resp);
        }
        this.showOptimize = true;
        this.activeCons = this.initActiveCon()
        this.list = this.assets;
        if (discard) {
          this.discardCon();
        }
      })
    }
  }

  private getProjectedConstraintsSnapshot() {
    const constraints = this.activeCons?.id ? this.constraints.filter(c => c.id !== this.activeCons.id) : [...this.constraints];

    if (!this.activeCons || !this.activeCons.type) {
      return constraints;
    }

    if (this.activeCons.saveEach && Array.isArray(this.activeCons.items) && this.activeCons.items.length) {
      const projected = [...constraints];
      this.activeCons.items.forEach(itemId => {
        projected.push({
          ...this.activeCons,
          items: [itemId]
        });
      });
      return projected;
    }

    return [
      ...constraints,
      { ...this.activeCons }
    ];
  }

  private normalizeConstraintType(constraint: any) {
    return constraint?.type?.id ?? constraint?.type;
  }

  private getConstraintItemIds(constraint: any): number[] {
    if (!constraint?.items?.length) {
      return [];
    }
    return constraint.items.map((item: any) => {
      if (item && typeof item === 'object') {
        return +item.id;
      }
      return +item;
    }).filter(id => !isNaN(id));
  }

  private hasImpossibleAssetMax(constraints: any[]): boolean {
    const assetsCount = this.portfolio?.securities?.length || 0;
    if (!assetsCount) {
      return false;
    }

    const assetConstraints = constraints.filter(c => this.normalizeConstraintType(c) === 'asset');
    if (!assetConstraints.length) {
      return false;
    }

    const maxByAsset = new Map<number, number>();

    assetConstraints.forEach(c => {
      const maxVal = +c.max || 0;
      this.getConstraintItemIds(c).forEach(id => {
        const existingMax = maxByAsset.get(id);
        if (existingMax === undefined) {
          maxByAsset.set(id, maxVal);
        } else {
          maxByAsset.set(id, Math.min(existingMax, maxVal));
        }
      });
    });

    if (maxByAsset.size !== assetsCount) {
      return false;
    }

    const totalMax = Array.from(maxByAsset.values()).reduce((sum, val) => sum + val, 0);
    return totalMax < 100;
  }

  private normalizePercentageValue(value: any): number | null | undefined {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    const parsed = Number(value);
    if (isNaN(parsed)) {
      return undefined;
    }
    return parsed;
  }

  private validateActiveConstraintValues(): boolean {
    if (!this.activeCons?.items?.length) {
      this.service.notificationService.open("Select at least one item to constrain");
      return false;
    }

    const min = this.normalizePercentageValue(this.activeCons.min);
    const max = this.normalizePercentageValue(this.activeCons.max);

    if (min === undefined || max === undefined) {
      this.service.notificationService.open("Min and max must be valid numbers");
      return false;
    }

    if (min === null && max === null) {
      this.service.notificationService.open("Enter a min and/or max percentage");
      return false;
    }

    if (min !== null && min < 0) {
      this.service.notificationService.open("Minimum cannot be negative");
      return false;
    }

    if (max !== null && max < 0) {
      this.service.notificationService.open("Maximum cannot be negative");
      return false;
    }

    if (min !== null && min > 100) {
      this.service.notificationService.open("Minimum cannot exceed 100%");
      return false;
    }

    if (max !== null && max > 100) {
      this.service.notificationService.open("Maximum cannot exceed 100%");
      return false;
    }

    if (min !== null && max !== null && min > max) {
      this.service.notificationService.open("Minimum cannot be greater than maximum");
      return false;
    }

    this.activeCons.min = min ?? undefined;
    this.activeCons.max = max ?? undefined;
    return true;
  }

  editCon(c) {
    this.activeCons = {...c};
    this.activeCons.type = c.type.id;
    this.onTypeChange();
    this.activeCons.items = c.items.map(i => +i.id);
    this.activeCons.portfolio_id = this.portfolio.id;
  }

  deleteCon(c) {
    this.service.delete(c).subscribe(resp => {
      this.constraints = this.constraints.filter(con => con.id !== c.id);
      this.showOptimize = true;
    })
  }

  discardCon() {
    this.activeCons = {};
  }
}
